import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { VRMLoaderPlugin, VRMUtils, type VRM, type VRMHumanBoneName } from '@pixiv/three-vrm';
import {
  fetchAnimation,
  fetchCategories,
  fetchPoseServerHealth,
  getPoseAnimationStatus,
  startPoseAnimation,
  updateAnimation,
  uploadAnimation,
  PoseAnimationUnreachableError,
  type CategoryInfo,
  type PoseAnimationJobStatus,
  type PoseAnimationKeyframeInput,
  type PoseAnimationResult,
  type PoseAnimationStatus,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { authEnabled } from '../auth/auth';
import DisplaySettingsModal from '../components/DisplaySettingsModal';
import PhotoConsentModal from '../components/PhotoConsentModal';
import PoseRecorder from '../components/PoseRecorder';
import PublishConfirmModal from '../components/PublishConfirmModal';
import Spinner from '../components/Spinner';
import UsageTermsModal from '../components/UsageTermsModal';
import VrmaPreview from '../components/viewer/VrmaPreview';
import type { VrmaPreviewHandle } from '../components/viewer/types';
import { useVrm } from '../context/VrmContext';
import { OFFICIAL_TAG } from '../data/officialTag';
import { generateVrmaThumbnail } from '../lib/generateThumbnail';
import { estimatePosesFromVideo } from '../lib/poseEstimation';
import { fusePoses, selectStillWindow, filterSharpFrames, type FusedPose } from '../lib/poseFusion';
import { solvePose } from '../lib/poseSolver';
import { decryptToBlobUrl } from '../lib/protectedAsset';
import { downscaleImage, estimatePoseWithAutoStart, extractSomaPose, toPoseMap } from '../lib/serverPose';
import { detectStorePlatform } from '../lib/storePlatform';
import { USAGE_TERMS_LEAD_TEXT, usageSummaryRows } from '../lib/usageTermsSummary';
import { exportAnimationVrma, exportPoseVrma, validateVrma, type VrmaAnimationFrame } from '../lib/vrmaExporter';
import type { Animation, AnimationCredit, Rating, UsageTerms, Visibility } from '../types';

// サムネイル生成のデフォルト再生位置（clip.duration に対する比率）。
const DEFAULT_THUMB_RATIO = 0.3;
const THUMBNAIL_MAX_BYTES = 2 * 1024 * 1024; // 2MB
const MAX_TAGS = 10; // 表示・投稿時の上限（バックエンドは後方互換のため 20 まで受け付ける）。
const MAX_STORE_URLS = 5; // 関連商品URLの登録上限。
const MAX_LICENSE_LENGTH = 1000; // ライセンス表記（自由記述）の上限文字数。バックエンドと揃える。

// ---- 複数画像からアニメーションを作成（サーバー推論・非同期ジョブ）関連の定数 ----
const MIN_ANIMATION_IMAGES = 2; // キーポーズは最低2枚（区間が1つ以上必要）
const MAX_ANIMATION_IMAGES = 6; // サーバー側の生成ジョブが受け付ける上限
const MIN_ANIMATION_INTERVAL_SEC = 1;
const MAX_ANIMATION_INTERVAL_SEC = 10; // サーバー側の1セグメントあたりの生成上限に合わせる
const DEFAULT_ANIMATION_INTERVAL_SEC = 5;
const MAX_ANIMATION_PROMPT_LENGTH = 300; // バックエンドの検証と揃える
// Kimodo の学習データがこの書式（英語・"A person..." で始まる一文）に寄っているため、
// 既定値もその形にしておくと安定しやすい。
const DEFAULT_ANIMATION_PROMPT = 'A person smoothly changes pose.';
const ANIMATION_POLL_INTERVAL_MS = 5_000; // ジョブ状態のポーリング間隔
// 推論サーバーへの一時的な不通（503・通信エラー）を連続何回まで許容してからエラー表示に
// 落とすか。自宅PCの Cloudflare Tunnel 経由のため瞬断が起こり得るが、ジョブ自体はサーバー側で
// 走り続けているため、ここで諦めると生成結果を取り逃がしてしまう。
const MAX_ANIMATION_POLL_FAILURES = 4;

// 新規登録時の既定値。個別許可はすべて false（＝最も保守的）、改変のみ true、クレジット表記は不要とする。
const DEFAULT_USAGE_TERMS: UsageTerms = {
  violent: false,
  sexual: false,
  corporate: false,
  commercial: false,
  redistribute: false,
  modify: true,
  creditRequired: false,
};

function formatTimestamp(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

/** タグ入力欄のテキストをタグ配列にパースする（カンマ・読点・空白区切り、空要素は除去）。 */
function parseTags(text: string): string[] {
  return text
    .split(/[,、\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

/**
 * アップロード画面（画像から作成／複数画像からアニメーションを作成）で使う pose-server の表示状態。
 * checking: 確認中 / online: 稼働中 / starting: 起動処理中 / idle: 停止中だが受付ONで投げれば自動起動する
 * / not-accepting: 管理者が受付を止めている / offline: それ以外（接続不可・未設定・異常など）。
 */
type PoseServerStatus = 'checking' | 'online' | 'starting' | 'idle' | 'not-accepting' | 'offline';

/**
 * fetchPoseServerHealth() の結果を画面表示用の状態に変換する。
 * running/starting はそのまま表示し（生成中に受付OFFへ切り替わっても直前の状態を優先する）、
 * それ以外は accepting を見て「投げれば自動起動する待機中(idle)」か「受付停止中」かを区別する。
 */
function derivePoseServerStatus(health: { state: string; accepting: boolean }): PoseServerStatus {
  if (health.state === 'running') return 'online';
  if (health.state === 'starting') return 'starting';
  if (!health.accepting) return 'not-accepting';
  if (health.state === 'stopped' || health.state === 'stopping') return 'idle';
  return 'offline';
}

/** poseServerStatus の表示文言。2箇所（画像から作成／アニメーション作成）で共有する。 */
function poseServerStatusLabel(status: PoseServerStatus): string {
  switch (status) {
    case 'checking':
      return '確認中…';
    case 'online':
      return '● サーバー起動中';
    case 'starting':
      return '● サーバーを起動しています…（通常1分ほど。初回のモデル読み込み時はもう少しかかります）';
    case 'idle':
      return '● 待機中 — 生成を開始するとサーバーが起動します（1分ほどかかります）';
    case 'not-accepting':
      return '● 現在ポーズ生成の受付を停止しています';
    case 'offline':
      return '● サーバーオフライン — 時間をおいてお試しください';
  }
}

/**
 * サーバー側のジョブ状態（PoseAnimationJobStatus）を日本語の一言に変換する。
 * サーバーが message を返していればそちらを優先して表示するため、これはあくまでフォールバック。
 */
function poseAnimationStatusLabel(status: PoseAnimationJobStatus | undefined): string {
  switch (status) {
    case 'queued':
      return '順番待ちです…';
    case 'stopping-pose-server':
      return '既存のサーバーを停止しています…';
    case 'starting-kimodo':
      return 'サーバーを起動しています…（初回はモデルのダウンロードで時間がかかります）';
    case 'generating':
      return '生成しています…';
    case 'done':
      return '完了しました';
    case 'failed':
      return '失敗しました';
    default:
      return '状態を確認しています…';
  }
}

/** レンダラー不要の headless で、正規化ヒューマノイド計算のためだけに VRM を読み込む。 */
async function loadHeadlessVrm(vrmUrl: string): Promise<VRM> {
  const loader = new GLTFLoader();
  loader.register((parser) => new VRMLoaderPlugin(parser));
  const gltf = await loader.loadAsync(vrmUrl);
  const vrm = gltf.userData.vrm as VRM;
  VRMUtils.removeUnnecessaryVertices(gltf.scene);
  VRMUtils.combineSkeletons(gltf.scene);
  VRMUtils.rotateVRM0(vrm);
  return vrm;
}

// 'false' のときだけ動画からのポーズ生成 UI を無効化する（本番限定の未公開機能フラグ）。未設定なら有効。
const poseGenerationEnabled = import.meta.env.VITE_ENABLE_POSE_GENERATION !== 'false';
// 画像からのポーズ生成（サーバー推論）は、動画からの生成とは独立に、専用フラグが立っていて、
// かつログイン機能自体が有効（GPU 保護のため未ログイン導線を作らない）な場合のみ表示する。
const serverPoseGenerationEnabled =
  import.meta.env.VITE_ENABLE_SERVER_POSE_GENERATION !== 'false' && authEnabled;
// 複数画像からのアニメーション生成も同じ pose-server（Kimodo）を使うため、画像からのポーズ生成が
// 有効であることを前提に、専用フラグが立っている場合のみ表示する。
const poseAnimationEnabled =
  serverPoseGenerationEnabled && import.meta.env.VITE_ENABLE_POSE_ANIMATION !== 'false';

export default function Upload() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const { user, ready, login } = useAuth();
  const { vrmUrl } = useVrm();

  // ---- 編集モード（既存アニメーションの読み込み） ----
  const [existing, setExisting] = useState<Animation | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(isEdit);
  const [loadError, setLoadError] = useState('');
  const [fileDirty, setFileDirty] = useState(false); // ポーズデータをユーザーが差し替えたか
  const [existingThumbUrl, setExistingThumbUrl] = useState('');

  // ---- 基本情報 ----
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tagsText, setTagsText] = useState('');
  const tagCount = parseTags(tagsText).length;
  // ---- カテゴリ（最大1つ、任意。未選択は「未分類」扱い）----
  const [category, setCategory] = useState(''); // 空文字列 = 未分類
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  useEffect(() => {
    fetchCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  // ---- 表示設定 ----
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [rating, setRating] = useState<Rating>('all');
  const [showDisplayModal, setShowDisplayModal] = useState(false);

  // ---- 投稿者以外の利用条件 ----
  const [usageTerms, setUsageTerms] = useState<UsageTerms>(DEFAULT_USAGE_TERMS);
  const [showUsageModal, setShowUsageModal] = useState(false);

  // ---- クレジット（出典・ライセンス表記。全欄空なら credit なしで投稿）----
  const [creditStoreUrls, setCreditStoreUrls] = useState<string[]>(['']);
  const [creditLicense, setCreditLicense] = useState('');
  const [creditError, setCreditError] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(''); // 投稿エラー（素材未確定・タイトル未入力・送信失敗）
  const [showPublishConfirm, setShowPublishConfirm] = useState(false); // 公開投稿時の確認モーダル

  // ---- 素材ソース ----
  const [source, setSource] = useState<'file' | 'video' | 'image' | 'animation'>('file');
  // 機能無効時・編集モード（既存ファイルの差し替えのみ対応）は、セグメント非表示で
  // 'video'/'image'/'animation' への切替経路自体を塞いでいるが、念のため描画側でも
  // 'file' 固定にフォールバックしておく。
  const effectiveSource =
    isEdit
      ? 'file'
      : source === 'video' && !poseGenerationEnabled
        ? 'file'
        : source === 'image' && !serverPoseGenerationEnabled
          ? 'file'
          : source === 'animation' && !poseAnimationEnabled
            ? 'file'
            : source;

  // 確定した素材（.vrma を直接選択 / 動画から生成のどちらでもここに入る）。
  const [file, setFile] = useState<File | null>(null);
  const [vrmaUrl, setVrmaUrl] = useState(''); // プレビュー用 objectURL（file から生成）
  const vrmaUrlRef = useRef('');

  // ---- .vrma ファイルソース ----
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---- 動画から作成ソース ----
  const [video, setVideo] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState(''); // プレビュー用 objectURL（video から生成）
  const videoUrlRef = useRef('');
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [stillFrameCount, setStillFrameCount] = useState(0); // 静止区間として統合に使ったフレーム数
  const [generating, setGenerating] = useState(false);
  const [mirror, setMirror] = useState(false);
  const [poseError, setPoseError] = useState(''); // 素材エラー（検出・生成失敗）
  const fusedRef = useRef<FusedPose | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null); // 解析中の estimatePosesFromVideo を中断するため
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [showRecorder, setShowRecorder] = useState(false); // ブラウザ内カメラ撮影（骨格オーバーレイ付き）モーダル

  // ---- 画像から作成ソース（サーバー推論）----
  const [image, setImage] = useState<File | null>(null); // 選択中の画像（1枚のみ）
  const [imageUrl, setImageUrl] = useState(''); // image のプレビュー用 objectURL
  const imageUrlRef = useRef('');
  const [imageDragging, setImageDragging] = useState(false);
  const [estimatingImage, setEstimatingImage] = useState(false);
  const [poseServerStatus, setPoseServerStatus] = useState<PoseServerStatus>('checking');
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [imageNotice, setImageNotice] = useState(''); // 複数枚選択時など、エラーではない軽い注意書き
  // true で画像推定ループ・estimatePoseWithAutoStart の起動待ちポーリングを打ち切る（ソース切替・
  // アンマウント用）。起動待ちは最大5分あるため、これを繋がないと数分後に adoptPose() が呼ばれ、
  // 既に別ソースへ切り替えた後のプレビューへポーズが突然差し替わる事故になる（animPollAbortRef と同じ理由）。
  const imageEstimateAbortRef = useRef(false);

  // ---- 複数画像からアニメーションを作成ソース（サーバー推論・非同期ジョブ）----
  // 画像は「画像から作成」（images）とは別の state で持つ（順序が姿勢の時系列そのものを表すため、
  // ソース切替時に混線しないよう独立させている）。
  const [animImages, setAnimImages] = useState<File[]>([]); // 選んだ順（先頭が1キーフレーム目）
  const [animImageUrls, setAnimImageUrls] = useState<string[]>([]); // animImages と同じ並びの objectURL
  const animImageUrlsRef = useRef<string[]>([]);
  const [animImageDragging, setAnimImageDragging] = useState(false);
  const animImageInputRef = useRef<HTMLInputElement>(null);
  const [animNotice, setAnimNotice] = useState(''); // 上限超過など、エラーではない軽い注意書き
  const [animPrompt, setAnimPrompt] = useState(DEFAULT_ANIMATION_PROMPT);
  const [animIntervalSec, setAnimIntervalSec] = useState(DEFAULT_ANIMATION_INTERVAL_SEC);
  const [animError, setAnimError] = useState(''); // 生成エラー（poseError とは別に持ち、他ソースと混線させない）
  // client 側フェーズ（画像1枚ずつの推定 → ジョブ開始 → ポーリング → VRMA 組み立て）。
  // サーバー側の詳細な状態（queued / generating 等）は animJobStatus に持つ。
  const [animPhase, setAnimPhase] = useState<
    'idle' | 'estimating-poses' | 'starting-job' | 'polling' | 'building-vrma'
  >('idle');
  const animRunning = animPhase !== 'idle';
  const [animEstimateProgress, setAnimEstimateProgress] = useState({ done: 0, total: 0 }); // 画像推定フェーズの進捗
  const [animJobStatus, setAnimJobStatus] = useState<PoseAnimationStatus | null>(null); // 直近のポーリング結果
  const [animPollWarning, setAnimPollWarning] = useState(''); // 一時的な接続不安定時のソフト警告（致命的ではない）
  const animPollAbortRef = useRef(false); // true でポーリングループを打ち切る（ソース切替・アンマウント用）
  const animPollTimeoutRef = useRef<number | null>(null); // ポーリング間隔待ちの setTimeout ハンドル

  // 人物写真をサーバーへ送ってポーズ推定する操作（「画像から作成」「複数画像からアニメーションを
  // 作成」の両方）の直前に挟む、肖像権配慮の確認モーダル。'image'/'animation' でどちらの操作を
  // 確認中かを表し、null なら非表示。ボタン押下では推定を始めず、ここに値を入れて表示するだけにする。
  const [consentTarget, setConsentTarget] = useState<'image' | 'animation' | null>(null);

  // ---- サムネイル関連 ----
  const [autoThumb, setAutoThumb] = useState<Blob | null>(null);
  const [autoThumbUrl, setAutoThumbUrl] = useState('');
  const [autoThumbNotice, setAutoThumbNotice] = useState('');
  const [generatingThumb, setGeneratingThumb] = useState(false);
  const [thumbRatio, setThumbRatio] = useState(DEFAULT_THUMB_RATIO); // 再生位置スライダー（0〜1）
  const [userThumb, setUserThumb] = useState<Blob | null>(null);
  const [userThumbUrl, setUserThumbUrl] = useState('');
  const [thumbError, setThumbError] = useState('');

  // ---- プレビュー装飾（視点リセット・操作ヘルプ・全画面） ----
  const previewWrapRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<VrmaPreviewHandle>(null);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    vrmaUrlRef.current = vrmaUrl;
  }, [vrmaUrl]);

  useEffect(() => {
    videoUrlRef.current = videoUrl;
  }, [videoUrl]);

  useEffect(() => {
    imageUrlRef.current = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    animImageUrlsRef.current = animImageUrls;
  }, [animImageUrls]);

  // 「画像から作成」「複数画像からアニメーションを作成」パネル表示中（マウント時 + ソース切替時）は
  // pose-server の稼働状況を 15 秒間隔でポーリングし、オンライン/オフライン・起動中の切り替わりを
  // 追従する（どちらも同じ pose-server / Kimodo を使うため同じバッジ・同じポーリングを共有する）。
  useEffect(() => {
    if (source !== 'image' && source !== 'animation') return;
    let cancelled = false;
    const refresh = () => {
      fetchPoseServerHealth().then((res) => {
        if (cancelled) return;
        setPoseServerStatus(derivePoseServerStatus(res));
      });
    };
    setPoseServerStatus('checking');
    refresh();
    const timer = setInterval(refresh, 15_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [source]);

  // アンマウント時に、その時点の最新 objectURL をまとめて revoke し、解析中なら中断する。
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
      if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
      animImageUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      if (vrmaUrlRef.current) URL.revokeObjectURL(vrmaUrlRef.current);
      // 生成中（画像推定〜ポーリング〜VRMA組み立て）ならポーリングループを打ち切る。
      animPollAbortRef.current = true;
      if (animPollTimeoutRef.current != null) window.clearTimeout(animPollTimeoutRef.current);
      // 「画像から作成」側も同様に、起動待ち中の estimatePoseWithAutoStart が
      // アンマウント後に adoptPose() まで到達しないよう打ち切る。
      imageEstimateAbortRef.current = true;
    };
    // マウント時にのみ登録するクリーンアップ（アンマウント時点の最新状態は ref 経由で参照する）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runGenerateThumbnail = async (vrmaFile: File, ratio: number) => {
    setGeneratingThumb(true);
    setAutoThumbNotice('');
    try {
      const blob = await generateVrmaThumbnail(vrmaFile, ratio, vrmUrl);
      setAutoThumb(blob);
      setAutoThumbUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch (e) {
      console.error(e);
      setAutoThumb(null);
      setAutoThumbUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      setAutoThumbNotice('自動生成できませんでした。画像を選択するかそのまま投稿できます。');
    } finally {
      setGeneratingThumb(false);
    }
  };

  // .vrma ソース・動画ソースいずれの経路も最終的にここへ合流する。
  // ここで確定した File が投稿対象になり、プレビュー用 objectURL・自動サムネ生成もまとめて行う。
  const handleFile = (f: File | undefined) => {
    if (!f) return;
    if (!/\.(vrma|glb)$/i.test(f.name)) {
      setError('.vrma ファイルを選択してください。');
      return;
    }
    setError('');
    setFile(f);
    setFileDirty(true); // ユーザーによる差し替え（初期ロードでの取り込みは applyExistingFile を使う）
    setVrmaUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
    if (!title) setTitle(f.name.replace(/\.(vrma|glb)$/i, ''));

    // ファイルを選び直したらサムネイル生成もやり直す（再生位置はスライダーの現在値を使う）。
    void runGenerateThumbnail(f, thumbRatio);
  };

  // 編集モードの初期ロード専用。handleFile と異なり、既存サムネイルを尊重するため
  // 自動サムネイル生成は走らせず、fileDirty も立てない。
  const applyExistingFile = (f: File) => {
    setFile(f);
    setVrmaUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
  };

  // 素材ソースを切り替える。タイトル・タグ・説明・表示設定は保持し、素材由来の state のみ破棄する。
  const handleSourceChange = (next: 'file' | 'video' | 'image' | 'animation') => {
    if (next === source) return;
    setFile(null);
    setVrmaUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setAutoThumb(null);
    setAutoThumbUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setAutoThumbNotice('');
    setThumbRatio(DEFAULT_THUMB_RATIO);
    setUserThumb(null);
    setUserThumbUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setThumbError('');
    abortControllerRef.current?.abort();
    setVideo(null);
    setVideoUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setStillFrameCount(0);
    setAnalyzing(false);
    setProgress({ done: 0, total: 0 });
    setGenerating(false);
    setMirror(false);
    setPoseError('');
    fusedRef.current = null;
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setImage(null);
    setImageUrl('');
    setImageDragging(false);
    setEstimatingImage(false);
    setImageNotice('');
    // 起動待ち中（estimatePoseWithAutoStart のポーリング）なら打ち切ってから state を破棄する。
    // 打ち切らないと、数分後に起動完了 → 再試行成功 → adoptPose() まで裏で進んでしまい、
    // 既に切り替えた別ソースのプレビューへポーズが何の操作もなく突然差し替わることになる。
    imageEstimateAbortRef.current = true;
    // 生成中（画像推定〜ポーリング〜VRMA組み立て）ならポーリングループを打ち切ってから state を破棄する。
    animPollAbortRef.current = true;
    if (animPollTimeoutRef.current != null) window.clearTimeout(animPollTimeoutRef.current);
    animImageUrls.forEach((u) => URL.revokeObjectURL(u));
    setAnimImages([]);
    setAnimImageUrls([]);
    setAnimImageDragging(false);
    setAnimNotice('');
    setAnimPrompt(DEFAULT_ANIMATION_PROMPT);
    setAnimIntervalSec(DEFAULT_ANIMATION_INTERVAL_SEC);
    setAnimError('');
    setAnimPhase('idle');
    setAnimEstimateProgress({ done: 0, total: 0 });
    setAnimJobStatus(null);
    setAnimPollWarning('');
    // 承諾確認モーダルを表示したままソースを切り替えると、別ソースに移った後に
    // OKを押して古いソースの推定/生成が始まってしまうため、ここで閉じておく。
    setConsentTarget(null);
    setError('');
    setSource(next);
  };

  // 編集モード: 既存のアニメーションを取得し、各 state に反映する。認証準備完了後に一度だけ実行する。
  useEffect(() => {
    if (!isEdit || !id || !ready) return;
    // 未ログインなら下のログインガードが表示されるため、ここでは読み込まない。
    if (authEnabled && !user) return;
    let cancelled = false;
    setLoadingExisting(true);
    setLoadError('');
    (async () => {
      try {
        const animation = await fetchAnimation(id);
        if (cancelled) return;
        if (!animation) {
          setLoadError('アニメーションが見つかりません');
          return;
        }
        if (authEnabled && user && user.sub !== animation.ownerId) {
          setLoadError('このポーズを編集する権限がありません。');
          return;
        }
        setExisting(animation);
        setTitle(animation.title);
        setDescription(animation.description);
        setTagsText(animation.tags.join(' '));
        setCategory(animation.category ?? '');
        setVisibility(animation.visibility ?? 'public');
        setRating(animation.rating ?? 'all');
        setUsageTerms(animation.usageTerms ?? DEFAULT_USAGE_TERMS);
        setCreditLicense(animation.credit?.license ?? '');
        // 新形式の storeUrls が無ければ、旧データ互換で sourceUrl を1件だけ表示する。
        // sourceUrl は AnimationCredit から廃止済みだが、旧データの応答には残り得るため型を拡張して読む。
        const legacySourceUrl = (animation.credit as ({ sourceUrl?: string } | undefined))?.sourceUrl;
        setCreditStoreUrls(
          animation.credit?.storeUrls?.length
            ? animation.credit.storeUrls
            : legacySourceUrl
              ? [legacySourceUrl]
              : [''],
        );
        setExistingThumbUrl(animation.thumbnailUrl ?? '');

        // 既存の .vrma を File として取り込む（再生プレビュー・差し替え不要時の再アップロード用）。
        // 失敗しても致命にせず、プレビューなし（file なし）でメタ情報だけ編集できる状態にする。
        if (animation.vrmaUrl) {
          try {
            const resolvedUrl = animation.encrypted
              ? await decryptToBlobUrl(animation.vrmaUrl)
              : animation.vrmaUrl;
            const res = await fetch(resolvedUrl);
            if (!res.ok) throw new Error(`取得に失敗しました (${res.status})`);
            const blob = await res.blob();
            const safeName = animation.title.replace(/[\\/:*?"<>|\x00-\x1f]/g, '_').trim() || animation.id;
            const existingFile = new File([blob], `${safeName}.vrma`, { type: 'application/octet-stream' });
            applyExistingFile(existingFile);
          } catch (e) {
            console.warn('既存のポーズデータの取得に失敗しました', e);
          }
        }
      } catch (e) {
        if (!cancelled) setLoadError(String(e));
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // マウント〜認証準備完了時の一度きりの読み込み。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, ready, user]);

  // 認証が有効な環境ではログイン必須。
  if (authEnabled && ready && !user) {
    return (
      <div className="container">
        <div className="section-title">
          <h2>{isEdit ? 'ポーズを編集' : 'ポーズを登録'}</h2>
        </div>
        <div className="notice" style={{ marginBottom: 20 }}>
          {isEdit ? '編集にはログインが必要です。' : 'アップロードにはログインが必要です。'}
        </div>
        <button className="btn btn--primary" onClick={login}>
          ログイン / 新規登録
        </button>
      </div>
    );
  }

  // 編集モードの初期ロード中・失敗時はフォームを出さない。
  if (isEdit && loadingExisting) {
    return (
      <div className="container">
        <Spinner />
      </div>
    );
  }

  if (isEdit && loadError) {
    return (
      <div className="container">
        <div className="viewer__error">{loadError}</div>
        <Link to="/">一覧へ戻る</Link>
      </div>
    );
  }

  const handleRegenerateThumb = () => {
    if (!file || generatingThumb) return;
    void runGenerateThumbnail(file, thumbRatio);
  };

  const handlePickThumbnail = (f: File | undefined) => {
    if (!f) return;
    setThumbError('');
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
      setThumbError('画像は png / jpeg / webp のみ選択できます。');
      return;
    }
    if (f.size > THUMBNAIL_MAX_BYTES) {
      setThumbError('画像サイズは 2MB 以下にしてください。');
      return;
    }
    setUserThumb(f);
    setUserThumbUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
  };

  const handleResetToAuto = () => {
    setUserThumb(null);
    setUserThumbUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setThumbError('');
  };

  // 採用中のサムネイル（ユーザー画像を優先）。編集モードは、自動生成も無ければ既存サムネイルを使う。
  const activeThumb = userThumb ?? autoThumb;
  const activeThumbUrl = userThumbUrl || autoThumbUrl || (isEdit ? existingThumbUrl : '');

  // ---- 動画から作成パイプライン ----
  // 選択された動画 File を受理する処理。「端末から選択」（input）とレコーダー（録画完了時）の
  // 両方から使う共通処理。録画 File は type が video/webm 等になるので同じ判定を通る。
  const acceptVideoFile = (f: File) => {
    if (!f.type.startsWith('video/')) {
      setPoseError('動画ファイルを選択してください。');
      return;
    }
    setPoseError('');
    setVideo(f);
    setVideoUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
    setStillFrameCount(0);
  };

  const onPickVideo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    acceptVideoFile(f);
  };

  /**
   * pose（ボーン回転の Map）→ headless VRM 読み込み → exportPoseVrma → validateVrma →
   * handleFile() までの共通処理。動画から生成した pose（solvePose の結果）・画像から
   * サーバー推定した pose（toPoseMap の結果）のどちらもここに合流させ、自動サムネ生成・
   * タイトル自動設定などの下流処理を共通化する。
   *
   * rootTranslation はサーバー推定（/estimate）が返す hips の並進。渡すと腰の高さが
   * 反映され、座り・しゃがみポーズが「脚は曲がっているのに腰が下がらない」状態に
   * ならない。動画からの生成（solvePose）は並進を返さないので渡さず、その場合は
   * 従来どおり VRM のレスト高さのままになる。
   */
  const adoptPose = async (
    pose: Map<VRMHumanBoneName, THREE.Quaternion>,
    rootTranslation?: { x: number; y: number; z: number },
  ) => {
    setGenerating(true);
    setPoseError('');
    let vrm: VRM | undefined;
    try {
      vrm = await loadHeadlessVrm(vrmUrl);
      const blob = exportPoseVrma(vrm, pose, rootTranslation);
      await validateVrma(blob, vrmUrl);

      const generated = new File([blob], `pose-${formatTimestamp(new Date())}.vrma`, {
        type: 'application/octet-stream',
      });
      handleFile(generated);
    } catch (e) {
      console.error(e);
      setPoseError(`VRMA の生成に失敗しました。${e instanceof Error ? e.message : String(e)}`);
    } finally {
      if (vrm) VRMUtils.deepDispose(vrm.scene);
      setGenerating(false);
    }
  };

  /** 動画からの生成専用: solvePose でポーズを解いてから adoptPose() に渡す。 */
  const runGeneratePoseVrma = async (fused: FusedPose, mirrorFlag: boolean) => {
    const pose = solvePose(fused, { mirror: mirrorFlag });
    await adoptPose(pose);
  };

  const handleEstimate = async () => {
    if (!video || analyzing) return;
    setAnalyzing(true);
    setPoseError('');
    setProgress({ done: 0, total: 0 });
    setStillFrameCount(0);

    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const samples = await estimatePosesFromVideo(video, {
        onProgress: (done, total) => setProgress({ done, total }),
        signal: controller.signal,
      });
      if (samples.length === 0) {
        setPoseError('動画からポーズを検出できませんでした。人物の全身が映るように撮り直してください。');
        return;
      }

      const stillWindow = selectStillWindow(samples);
      const sharpWindow = filterSharpFrames(stillWindow);
      setStillFrameCount(sharpWindow.length);
      const fused = fusePoses(sharpWindow);
      fusedRef.current = fused;
      await runGeneratePoseVrma(fused, mirror);
    } catch (e) {
      // ユーザーによる中断はエラー表示しない。
      if (e instanceof DOMException && e.name === 'AbortError') return;
      console.error(e);
      setPoseError(`ポーズの検出に失敗しました。${e instanceof Error ? e.message : String(e)}`);
    } finally {
      abortControllerRef.current = null;
      setAnalyzing(false);
    }
  };

  const handleCancelEstimate = () => {
    abortControllerRef.current?.abort();
  };

  const handleToggleMirror = async () => {
    const next = !mirror;
    setMirror(next);
    if (fusedRef.current) {
      await runGeneratePoseVrma(fusedRef.current, next);
    }
  };

  // ---- 画像から作成パイプライン（サーバー推論）----
  // 画像を1枚受理する。複数選択・複数ドロップされた場合は先頭の1枚だけを採用し、その旨を表示する。
  // ドロップゾーン・input どちらからも使う共通処理。
  const acceptImageFiles = (files: FileList | File[]) => {
    const incoming = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (incoming.length === 0) return;
    const f = incoming[0];
    if (incoming.length > 1) {
      setImageNotice('画像は1枚だけ使います。');
    } else {
      setImageNotice('');
    }
    setImage(f);
    setImageUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
  };

  const onPickImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    // input.value = '' より前に File を取り出しておく（クリア後は e.target.files が空になるため）。
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) acceptImageFiles([f]);
  };

  const removeImage = () => {
    setImage(null);
    setImageUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setImageNotice('');
  };

  /**
   * 選択した画像を縮小・base64化してサーバーへ送り、推定結果を toPoseMap() → adoptPose() に合流させる。
   */
  const handleEstimateImage = async () => {
    if (!image || estimatingImage) return;
    setEstimatingImage(true);
    setPoseError('');
    imageEstimateAbortRef.current = false;
    try {
      const { base64, mimeType } = await downscaleImage(image);
      // imageEstimateAbortRef を isAborted に渡し、起動待ちポーリング中の中断にも対応する。
      // abortControllerRef は動画からの推定（estimatePosesFromVideo）専用の中断シグナルなので
      // ここでは使わない。サーバー起動待ちに入った場合は onWaitingForServer でバッジを
      // 「起動中」に切り替える。
      const result = await estimatePoseWithAutoStart(
        { base64, mimeType },
        {
          onWaitingForServer: () => setPoseServerStatus('starting'),
          isAborted: () => imageEstimateAbortRef.current,
        },
      );
      const pose = toPoseMap(result.bones);
      await adoptPose(pose, result.rootTranslation);
    } catch (e) {
      // ソース切替・アンマウントによる中断はエラー表示しない。中断中かどうかは ref の値で
      // 確実に判定する（アニメーション側の animPollAbortRef と同じ考え方）。
      if (!imageEstimateAbortRef.current) {
        console.error(e);
        setPoseError(`ポーズの推定に失敗しました。${e instanceof Error ? e.message : String(e)}`);
      }
    } finally {
      // 中断された場合もここは通す。中断経路（ソース切替）側でも同じ値に落としているため
      // 二重に呼んでも害はない。逆にここをガードすると、ref だけ立てて state を戻さない
      // 中断経路を将来足したときに「解析中」のまま固まる。
      setEstimatingImage(false);
    }
  };

  // ---- 複数画像からアニメーションを作成パイプライン（サーバー推論・非同期ジョブ）----
  // 画像（2〜6枚、順序付き）を受理する。ドロップゾーン・input どちらからも使う共通処理。
  const acceptAnimImageFiles = (files: FileList | File[]) => {
    const incoming = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (incoming.length === 0) return;
    const remaining = MAX_ANIMATION_IMAGES - animImages.length;
    const accepted = incoming.slice(0, Math.max(0, remaining));
    if (incoming.length > accepted.length) {
      setAnimNotice(`画像は最大 ${MAX_ANIMATION_IMAGES} 枚までです。超えた分は追加されませんでした。`);
    } else {
      setAnimNotice('');
    }
    if (accepted.length === 0) return;
    setAnimImages((prev) => [...prev, ...accepted]);
    setAnimImageUrls((prev) => [...prev, ...accepted.map((f) => URL.createObjectURL(f))]);
  };

  const onPickAnimImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    // onPickImages と同じ理由で、value をクリアする前に FileList を配列へコピーする。
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length > 0) acceptAnimImageFiles(files);
  };

  const removeAnimImage = (index: number) => {
    setAnimImages((prev) => prev.filter((_, i) => i !== index));
    setAnimImageUrls((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
    setAnimNotice('');
  };

  /** index 番目の画像を1つ前/後ろへ入れ替える（並び順＝キーポーズの時系列そのものを変える）。 */
  const moveAnimImage = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= animImages.length) return;
    const swap = <T,>(arr: T[]): T[] => {
      const next = [...arr];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    };
    setAnimImages(swap);
    setAnimImageUrls(swap);
  };

  /**
   * ジョブ完了まで数秒間隔でポーリングする。推論サーバーへの一時的な不通
   * （PoseAnimationUnreachableError = 503・通信エラー）は MAX_ANIMATION_POLL_FAILURES 回まで
   * 連続して許容し、その間はソフトな警告を出すだけで継続する（自宅PCのトンネル瞬断でジョブの
   * 生成結果を取り逃がさないため）。404・status: 'failed' は確定的な失敗として即座に投げる。
   * animPollAbortRef が立っていれば（ソース切替・アンマウント）AbortError を投げて中断する。
   */
  const pollPoseAnimation = async (jobId: string): Promise<PoseAnimationResult> => {
    let consecutiveFailures = 0;
    for (;;) {
      if (animPollAbortRef.current) throw new DOMException('中断されました', 'AbortError');
      try {
        const status = await getPoseAnimationStatus(jobId);
        consecutiveFailures = 0;
        setAnimPollWarning('');
        setAnimJobStatus(status);
        if (status.status === 'done') {
          if (!status.result) throw new Error('生成結果が空でした。');
          return status.result;
        }
        if (status.status === 'failed') {
          throw new Error(status.error ?? status.message ?? '生成に失敗しました。');
        }
      } catch (e) {
        if (e instanceof PoseAnimationUnreachableError) {
          consecutiveFailures += 1;
          if (consecutiveFailures >= MAX_ANIMATION_POLL_FAILURES) {
            throw new Error(`推論サーバーに接続できません。${e.message}`);
          }
          setAnimPollWarning('サーバーへの接続が不安定です。自動的に再試行しています…');
        } else {
          throw e;
        }
      }
      if (animPollAbortRef.current) throw new DOMException('中断されました', 'AbortError');
      await new Promise<void>((resolve) => {
        animPollTimeoutRef.current = window.setTimeout(resolve, ANIMATION_POLL_INTERVAL_MS);
      });
    }
  };

  /**
   * 画像を1枚ずつ順番に estimatePoseWithAutoStart()（pose-server 起動待ちを自動化した
   * generatePoseFromImage() のラッパー）へ送って SomaPose（77関節）を集め、
   * startPoseAnimation() でジョブを開始し、pollPoseAnimation() で完了を待ってから
   * exportAnimationVrma() → validateVrma() → handleFile() までを行う。
   * 静止ポーズ経路（adoptPose）とは異なり時系列のフレーム列を扱うため、専用の関数として持つ。
   */
  const handleGenerateAnimation = async () => {
    if (animImages.length < MIN_ANIMATION_IMAGES || animRunning) return;
    setAnimError('');
    setAnimPollWarning('');
    setAnimJobStatus(null);
    animPollAbortRef.current = false;
    setAnimPhase('estimating-poses');
    setAnimEstimateProgress({ done: 0, total: animImages.length });
    let vrm: VRM | undefined;
    try {
      const keyframes: PoseAnimationKeyframeInput[] = [];
      for (let i = 0; i < animImages.length; i++) {
        if (animPollAbortRef.current) throw new DOMException('中断されました', 'AbortError');
        try {
          const { base64, mimeType } = await downscaleImage(animImages[i]);
          // animPollAbortRef はこのフロー全体の中断フラグ（ソース切替・アンマウント時に立つ）。
          // 起動待ちに入った場合に打ち切りが効くよう isAborted に渡す。
          const result = await estimatePoseWithAutoStart(
            { base64, mimeType },
            {
              onWaitingForServer: () => setPoseServerStatus('starting'),
              isAborted: () => animPollAbortRef.current,
            },
          );
          const soma = extractSomaPose(result);
          keyframes.push({ localJointsRot: soma.localJointsRot, rootPosition: soma.rootPosition });
        } catch (e) {
          throw new Error(`${i + 1}枚目: ${e instanceof Error ? e.message : String(e)}`);
        }
        setAnimEstimateProgress({ done: i + 1, total: animImages.length });
      }

      setAnimPhase('starting-job');
      const { jobId } = await startPoseAnimation({
        keyframes,
        intervalSeconds: animIntervalSec,
        prompt: animPrompt.trim() || DEFAULT_ANIMATION_PROMPT,
      });

      setAnimPhase('polling');
      const result = await pollPoseAnimation(jobId);

      setAnimPhase('building-vrma');
      const frames: VrmaAnimationFrame[] = result.frames.map((f) => ({
        timeSec: f.timeSec,
        pose: toPoseMap(f.bones),
        rootTranslation: f.rootTranslation,
      }));
      vrm = await loadHeadlessVrm(vrmUrl);
      const blob = exportAnimationVrma(vrm, frames);
      await validateVrma(blob, vrmUrl);
      const generated = new File([blob], `animation-${formatTimestamp(new Date())}.vrma`, {
        type: 'application/octet-stream',
      });
      handleFile(generated);
    } catch (e) {
      // ソース切替・アンマウントによる中断はエラー表示しない。
      if (e instanceof DOMException && e.name === 'AbortError') return;
      console.error(e);
      setAnimError(`アニメーションの生成に失敗しました。${e instanceof Error ? e.message : String(e)}`);
    } finally {
      if (vrm) VRMUtils.deepDispose(vrm.scene);
      if (!animPollAbortRef.current) {
        setAnimPhase('idle');
        setAnimEstimateProgress({ done: 0, total: 0 });
      }
    }
  };

  const handleCancelPhotoConsent = () => {
    setConsentTarget(null);
  };

  // 確認モーダルの「同意して続ける」。対象を確定させてから閉じ、対応する推定/生成処理を開始する
  // （setConsentTarget を先に呼ぶことで、OK連打による二重起動を防ぐ）。
  const handleConfirmPhotoConsent = () => {
    const target = consentTarget;
    setConsentTarget(null);
    if (target === 'image') void handleEstimateImage();
    else if (target === 'animation') void handleGenerateAnimation();
  };

  /** クレジット欄が全欄空かどうか。空なら credit なしで投稿する。 */
  const creditIsEmpty = !creditLicense.trim() && creditStoreUrls.every((u) => !u.trim());

  /**
   * creditIsEmpty なら undefined。入力された項目だけで credit を組み立てる
   * （各項目は任意。空欄のフィールドは含めない）。入力値は onSubmit で検証済みの前提。
   */
  const buildCredit = (): AnimationCredit | undefined => {
    if (creditIsEmpty) return undefined;
    const license = creditLicense.trim();
    const storeUrls = creditStoreUrls.map((u) => u.trim()).filter(Boolean);
    return {
      ...(license ? { license } : {}),
      ...(storeUrls.length ? { storeUrls } : {}),
    };
  };

  // 実際の送信処理。公開確認モーダルの承認時／非公開の即時送信のどちらからも呼ばれる。
  // 新規登録は uploadAnimation、編集は updateAnimation を呼ぶ。
  // rightsAttested は、公開確認モーダルで権利者確認・利用規約同意の両方にチェックして
  // 確定した場合のみ true で渡される（handleConfirmPublish 経由）。
  const submit = async (opts?: { rightsAttested?: boolean }) => {
    if (!isEdit && !file) {
      setError('素材となる .vrma を用意してください。');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const tags = parseTags(tagsText);
      if (isEdit && id) {
        // ファイルを差し替えたときだけ再アップロードする。
        const needsFileUpload = !!file && fileDirty;
        // サムネイルは、ユーザーが画像を選んだとき／自動生成し直した（差し替え・再生成）ときだけ送る。
        // 初期ロードでは自動生成を走らせないため、autoThumb があれば画面上で作り直したものになる。
        const thumbnail = userThumb ?? autoThumb;
        const updated = await updateAnimation(id, {
          title: title.trim(),
          description: description.trim(),
          visibility,
          tags,
          category,
          file: needsFileUpload ? file : undefined,
          thumbnail: thumbnail ?? undefined,
          credit: creditIsEmpty ? null : buildCredit(),
          usageTerms,
          rating,
          rightsAttested: opts?.rightsAttested,
        });
        navigate(`/animations/${updated.id}`);
      } else if (file) {
        const created = await uploadAnimation({
          title: title.trim(),
          description: description.trim(),
          visibility,
          tags,
          ...(category ? { category } : {}),
          file,
          thumbnail: activeThumb,
          credit: buildCredit(),
          usageTerms,
          rating,
          rightsAttested: opts?.rightsAttested,
        });
        navigate(`/animations/${created.id}`);
      }
    } catch (e) {
      setError(String(e));
      setSubmitting(false);
      setShowPublishConfirm(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEdit && !file) {
      setError('素材となる .vrma を用意してください。');
      return;
    }
    if (!title.trim()) {
      setError('タイトルを入力してください。');
      return;
    }
    const tags = parseTags(tagsText);
    if (tags.includes(OFFICIAL_TAG) && !user?.isAdmin) {
      setError('「公式サンプル」タグを付けて投稿できるのは管理者のみです。');
      return;
    }
    if (tags.length > MAX_TAGS) {
      setError(`タグは${MAX_TAGS}個までです。`);
      return;
    }
    if (!creditIsEmpty) {
      const urlPattern = /^https?:\/\//i;
      const storeUrls = creditStoreUrls.map((u) => u.trim()).filter(Boolean);
      if (storeUrls.some((u) => !urlPattern.test(u))) {
        setCreditError('関連商品URLは http:// または https:// から始まる URL を入力してください。');
        return;
      }
    }
    setCreditError('');
    setError('');
    // 公開のときだけ確認モーダルを挟む。編集時は「非公開→公開」に変えるとき、または
    // 既存の投稿がまだ権利者確認（rightsAttestedAt）を持たないまま公開になるときに挟み、
    // それ以外（公開のまま／非公開のまま／公開→非公開）はそのまま送信する。
    const shouldConfirmPublish = isEdit
      ? visibility === 'public' &&
        ((existing?.visibility ?? 'public') === 'private' || !existing?.rightsAttestedAt)
      : visibility === 'public';
    if (shouldConfirmPublish) {
      setShowPublishConfirm(true);
      return;
    }
    void submit();
  };

  const handleCancelPublish = () => {
    setShowPublishConfirm(false);
  };

  // 公開確認モーダルは権利者確認・利用規約同意の両方がチェックされるまで確定できないため、
  // ここに来た時点で rightsAttested: true を確定させてよい。
  const handleConfirmPublish = () => {
    void submit({ rightsAttested: true });
  };

  const handleCancelPage = () => {
    navigate(-1);
  };

  const handleToggleFullscreen = () => {
    const el = previewWrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen?.();
    }
  };

  const previewMetaText =
    effectiveSource === 'video'
      ? `1 秒ループ · 静止区間 ${Math.max(stillFrameCount, 1)} フレームを統合`
      : effectiveSource === 'image'
        ? '1 秒ループ · 画像から推定したポーズを再生します'
        : effectiveSource === 'animation'
          ? `複数画像から生成したアニメーション（${animIntervalSec * Math.max(animImages.length - 1, 1)} 秒）`
          : '1 秒ループ · アップロードしたファイルをそのまま再生します';

  // VrmaPreview 本体＋操作アイコン・ヘルプを重ねたプレビューの塊。
  // file/video ソースの確定後表示と、画像ソースの右カラム（推定済み時）の両方から参照する。
  const posePreviewBlock = (
    <div className="upload-preview" ref={previewWrapRef}>
      {/* この画面はライト版固定にする。理由: (1) 既に MediaPipe wasm + ポーズ推定 + three.js
          で重く、Unity のヒープも載せると作業内容を失うと最も痛いフローで OOM リスクを負う。
          (2) プレビューの用途が「自分のポーズが正しいか確認する」ことなので見栄えの
          リッチ化の価値が低い。(3) .upload-preview__icons（top:12,right:12,z-index:2）と
          リッチ版トグル（.viewer__quality も右上）の z-index 衝突、および Unity
          シングルトンの消費者が2つになる問題を避けられる。 */}
      <VrmaPreview vrmaUrl={vrmaUrl} ref={previewRef} showQualityToggle={false} forceQuality="light" />

      <div className="upload-preview__icons">
        <button
          type="button"
          className="upload-icon-btn"
          title="視点をリセット"
          aria-label="視点をリセット"
          onClick={() => previewRef.current?.resetCamera()}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path
              d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          className="upload-icon-btn"
          title="操作方法"
          aria-label="操作方法"
          onClick={() => setShowHelp(true)}
        >
          ?
        </button>
        <button
          type="button"
          className="upload-icon-btn"
          title="全画面"
          aria-label="全画面"
          onClick={handleToggleFullscreen}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {showHelp && (
        <div className="upload-help-overlay" onClick={() => setShowHelp(false)}>
          <dl className="upload-help-list">
            <div>
              <dt>回転</dt>
              <dd>左クリック＋ドラッグ（スマホ: スワイプ）</dd>
            </div>
            <div>
              <dt>ズーム</dt>
              <dd>マウススクロール（スマホ: ピンチ）</dd>
            </div>
            <div>
              <dt>移動</dt>
              <dd>右クリック＋ドラッグ</dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );

  return (
    <div className="upload-page">
      <div className="upload-shell">
        {/* ---- 左ペイン: 基本情報 ---- */}
        <section className="upload-pane upload-pane--left">
          <h1 className="upload-pane__title">{isEdit ? 'ポーズを編集' : 'ポーズを登録'}</h1>

          {!authEnabled && (
            <div className="notice" style={{ marginBottom: 16 }}>
              バックエンド未接続のため、アップロードはブラウザ内に一時保存され、その場でプレビューできます。
            </div>
          )}

          <form id="pose-upload-form" className="upload-form-fields" onSubmit={onSubmit}>
            <div className="field">
              <label htmlFor="title">ポーズ名 ★</label>
              <input
                id="title"
                value={title}
                placeholder="例) 決めポーズ ver1"
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {(file || isEdit) && (
              <div className="field">
                <label>サムネイル ★</label>
                <div className="thumb-picker">
                  <div className="thumb-picker__preview">
                    {generatingThumb ? (
                      <span>サムネイル生成中…</span>
                    ) : activeThumbUrl ? (
                      <img src={activeThumbUrl} alt="サムネイルプレビュー" />
                    ) : (
                      <span>サムネイル未設定</span>
                    )}
                  </div>
                  <div className="thumb-picker__actions">
                    {file && (
                      <div className="thumb-picker__ratio">
                        <label htmlFor="thumb-ratio">再生位置 {Math.round(thumbRatio * 100)}%</label>
                        <input
                          id="thumb-ratio"
                          type="range"
                          min={0}
                          max={100}
                          step={1}
                          value={Math.round(thumbRatio * 100)}
                          onChange={(e) => setThumbRatio(Number(e.target.value) / 100)}
                          disabled={generatingThumb}
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn"
                      onClick={handleRegenerateThumb}
                      disabled={generatingThumb || !file}
                    >
                      この位置で再生成
                    </button>
                    <label className="btn">
                      画像を選択
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        hidden
                        onChange={(e) => handlePickThumbnail(e.target.files?.[0])}
                      />
                    </label>
                    {userThumb && (
                      <button type="button" className="btn" onClick={handleResetToAuto}>
                        自動生成に戻す
                      </button>
                    )}
                    {userThumb && <div className="thumb-picker__hint">画像を優先して使用します</div>}
                    {autoThumbNotice && !userThumb && (
                      <div className="thumb-picker__hint">{autoThumbNotice}</div>
                    )}
                    {thumbError && <div className="viewer__error">{thumbError}</div>}
                  </div>
                </div>
              </div>
            )}

            <div className="field">
              <div className="upload-field-label-row">
                <label htmlFor="tags">タグ</label>
                <span className={`upload-tag-count${tagCount > MAX_TAGS ? ' is-over' : ''}`}>
                  {tagCount}/{MAX_TAGS}
                </span>
              </div>
              <input
                id="tags"
                value={tagsText}
                placeholder="検索結果に表示されやすくなります。"
                onChange={(e) => setTagsText(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="category">カテゴリ</label>
              <select id="category" value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">未分類（指定なし）</option>
                {categories.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="desc">ポーズの説明</label>
              <textarea
                id="desc"
                rows={4}
                value={description}
                placeholder="このポーズの特徴を、みんなに紹介しましょう。"
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <hr className="upload-divider" />

            {/* 出典・ライセンス表記。チェックボックスは廃止し常時表示。全欄空なら credit なしで投稿する。 */}
            <div className="field">
              <h3 className="upload-credit-heading">出典・ライセンス表記</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="field">
                  <label htmlFor="credit-license">ライセンス</label>
                  <textarea
                    id="credit-license"
                    rows={3}
                    maxLength={MAX_LICENSE_LENGTH}
                    placeholder="例: MIT License / CC BY 4.0 / 独自の利用条件文"
                    value={creditLicense}
                    onChange={(e) => setCreditLicense(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="credit-store-url-0">関連商品URL</label>
                  {creditStoreUrls.map((url, i) => {
                    const platform = url.trim() ? detectStorePlatform(url) : null;
                    return (
                      <div
                        key={i}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}
                      >
                        <input
                          id={`credit-store-url-${i}`}
                          type="url"
                          placeholder="https://xxx.booth.pm/items/…"
                          style={{ flex: '1 1 auto', minWidth: 0 }}
                          value={url}
                          onChange={(e) => {
                            const next = [...creditStoreUrls];
                            next[i] = e.target.value;
                            setCreditStoreUrls(next);
                          }}
                        />
                        {platform && <span className="store-badge">{platform.label}</span>}
                        {creditStoreUrls.length > 1 && (
                          <button
                            type="button"
                            className="btn"
                            onClick={() => setCreditStoreUrls(creditStoreUrls.filter((_, idx) => idx !== i))}
                          >
                            削除
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {creditStoreUrls.length < MAX_STORE_URLS && (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setCreditStoreUrls([...creditStoreUrls, ''])}
                    >
                      + URLを追加
                    </button>
                  )}
                </div>
                {creditError && <div className="viewer__error">{creditError}</div>}
              </div>
            </div>

            {error && <div className="viewer__error">{error}</div>}
          </form>
        </section>

        {/* ---- 中央ペイン: プレビュー ---- */}
        <section className="upload-pane upload-pane--center">
          {!isEdit && (poseGenerationEnabled || serverPoseGenerationEnabled) && (
            <div className="upload-seg" aria-label="素材の種類">
              <button
                type="button"
                aria-pressed={source === 'file'}
                className={source === 'file' ? 'on' : ''}
                onClick={() => handleSourceChange('file')}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M6 2h9l5 5v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                .vrma ファイル
              </button>
              {poseGenerationEnabled && (
                <button
                  type="button"
                  aria-pressed={source === 'video'}
                  className={source === 'video' ? 'on' : ''}
                  onClick={() => handleSourceChange('video')}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M4 8h3l2-2h6l2 2h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path d="m14.5 10.5 5-2.5v8l-5-2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  動画から作成
                </button>
              )}
              {serverPoseGenerationEnabled && (
                <button
                  type="button"
                  aria-pressed={source === 'image'}
                  className={source === 'image' ? 'on' : ''}
                  onClick={() => handleSourceChange('image')}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect
                      x="3"
                      y="4"
                      width="18"
                      height="16"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <circle cx="8.5" cy="9.5" r="1.5" stroke="currentColor" strokeWidth="2" />
                    <path
                      d="m4 16 4.5-4.5a2 2 0 0 1 2.8 0L15 15m3-2 2 2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  画像から作成
                </button>
              )}
              {poseAnimationEnabled && (
                <button
                  type="button"
                  aria-pressed={source === 'animation'}
                  className={source === 'animation' ? 'on' : ''}
                  onClick={() => handleSourceChange('animation')}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect
                      x="3"
                      y="4"
                      width="7"
                      height="7"
                      rx="1.2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <rect
                      x="14"
                      y="13"
                      width="7"
                      height="7"
                      rx="1.2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path d="M10 7h4a3 3 0 0 1 3 3v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="m15 11 2 2 2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  複数画像からアニメーション
                </button>
              )}
            </div>
          )}

          {/* .vrma / .glb を確定させる隠し input。ドロップゾーン・右ペインの差し替えボタンから共通で使う。 */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".vrma,.glb"
            hidden
            onChange={(e) => handleFile(e.target.files?.[0])}
          />

          {effectiveSource === 'image' ? (
            <div className="upload-image-split">
              {/* 左カラム: 状態表示・画像リスト・ドロップゾーン・推定ボタン。推定完了後も表示したままにし、
                  画像を差し替えて再推定できるようにする。 */}
              <div className="upload-image-split__left">
                <p className={`upload-pose-server-status upload-pose-server-status--${poseServerStatus}`}>
                  {poseServerStatusLabel(poseServerStatus)}
                </p>

                {/* 画像未選択時はプレースホルダー、選択後は同じ枠内に写真を表示する。
                    どちらの状態でもクリック／ドラッグ＆ドロップでの差し替えを受け付ける。 */}
                <div
                  className={`upload-dropzone${image ? ' upload-dropzone--filled' : ''}${imageDragging ? ' drag' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (estimatingImage) return;
                    setImageDragging(true);
                  }}
                  onDragLeave={() => setImageDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setImageDragging(false);
                    if (estimatingImage) return;
                    if (e.dataTransfer.files?.length) acceptImageFiles(e.dataTransfer.files);
                  }}
                  onClick={() => {
                    if (estimatingImage) return;
                    imageInputRef.current?.click();
                  }}
                >
                  {image ? (
                    <div className="upload-dropzone__preview">
                      {imageUrl && <img src={imageUrl} alt="選択中の画像のプレビュー" />}
                      <button
                        type="button"
                        className="upload-image-item__remove"
                        aria-label="画像を削除"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage();
                        }}
                        disabled={estimatingImage}
                      >
                        ×
                      </button>
                      <p className="upload-dropzone__hint">クリックまたはドラッグ＆ドロップで差し替えできます</p>
                    </div>
                  ) : (
                    <>
                      <div className="upload-dropzone__icon" aria-hidden="true">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                          <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </div>
                      <p className="upload-dropzone__title">ここに画像を追加</p>
                      <p className="upload-dropzone__desc">ドラッグ＆ドロップ、またはクリックして選択（1枚）</p>
                    </>
                  )}
                </div>

                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={onPickImages}
                />

                {imageNotice && <div className="thumb-picker__hint">{imageNotice}</div>}

                <div className="upload-image-estimate-bar">
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => setConsentTarget('image')}
                    disabled={
                      !image ||
                      estimatingImage ||
                      generating ||
                      poseServerStatus === 'offline' ||
                      poseServerStatus === 'not-accepting' ||
                      poseServerStatus === 'starting'
                    }
                  >
                    ポーズを推定する（サーバー処理）
                  </button>
                  {estimatingImage && (
                    <Spinner inline size="sm" label="サーバーで推定中…（通常 5〜15 秒）" />
                  )}
                  {generating && (
                    <span className="thumb-picker__hint" style={{ marginLeft: 10 }}>
                      VRMA を生成中…
                    </span>
                  )}
                </div>

                {poseError && <div className="viewer__error">{poseError}</div>}
              </div>

              {/* 右カラム: ポーズプレビュー。推定前は空状態、推定後は共通のプレビュー塊を表示する。 */}
              <div className="upload-image-split__right">
                {file && vrmaUrl ? (
                  <>
                    {posePreviewBlock}
                    <p className="upload-preview-meta">{previewMetaText}</p>
                  </>
                ) : (
                  <div className="upload-pose-pane-empty">
                    <p>ポーズを推定するとここにプレビューが表示されます</p>
                  </div>
                )}
              </div>
            </div>
          ) : effectiveSource === 'animation' ? (
            <div className="upload-image-split">
              {/* 左カラム: 状態表示・画像リスト（並べ替え可）・プロンプト・間隔・生成ボタン。 */}
              <div className="upload-image-split__left">
                <p className={`upload-pose-server-status upload-pose-server-status--${poseServerStatus}`}>
                  {poseServerStatusLabel(poseServerStatus)}
                </p>

                <div className="upload-image-count">
                  画像 {animImages.length}/{MAX_ANIMATION_IMAGES}（最低 {MIN_ANIMATION_IMAGES} 枚）
                </div>
                <p className="thumb-picker__hint">
                  上から順に {animIntervalSec} 秒おきのポーズになります。矢印ボタンで並べ替えられます。
                </p>

                {animImages.length > 0 && (
                  <div className="upload-image-items">
                    {animImages.map((f, i) => (
                      <div className="upload-anim-image-item" key={`${f.name}-${i}`}>
                        <div className="upload-image-item">
                          {animImageUrls[i] && <img src={animImageUrls[i]} alt={`${i + 1}枚目のプレビュー`} />}
                          <span className="upload-image-item__badge" aria-hidden="true">
                            {i + 1}
                          </span>
                          <button
                            type="button"
                            className="upload-image-item__remove"
                            aria-label={`${i + 1}枚目を削除`}
                            onClick={() => removeAnimImage(i)}
                            disabled={animRunning}
                          >
                            ×
                          </button>
                        </div>
                        <div className="upload-anim-image-item__reorder">
                          <button
                            type="button"
                            aria-label={`${i + 1}枚目を1つ前へ`}
                            onClick={() => moveAnimImage(i, -1)}
                            disabled={animRunning || i === 0}
                          >
                            ←
                          </button>
                          <button
                            type="button"
                            aria-label={`${i + 1}枚目を1つ後ろへ`}
                            onClick={() => moveAnimImage(i, 1)}
                            disabled={animRunning || i === animImages.length - 1}
                          >
                            →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {animImages.length < MAX_ANIMATION_IMAGES && (
                  <div
                    className={`upload-dropzone${animImageDragging ? ' drag' : ''}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setAnimImageDragging(true);
                    }}
                    onDragLeave={() => setAnimImageDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setAnimImageDragging(false);
                      if (e.dataTransfer.files?.length) acceptAnimImageFiles(e.dataTransfer.files);
                    }}
                    onClick={() => animImageInputRef.current?.click()}
                  >
                    <div className="upload-dropzone__icon" aria-hidden="true">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </div>
                    <p className="upload-dropzone__title">ここに画像を追加</p>
                    <p className="upload-dropzone__desc">
                      ドラッグ＆ドロップ、またはクリックして選択（{MIN_ANIMATION_IMAGES}〜{MAX_ANIMATION_IMAGES} 枚、選んだ順がポーズの時系列になります）
                    </p>
                  </div>
                )}

                <input
                  ref={animImageInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  multiple
                  hidden
                  onChange={onPickAnimImages}
                />

                {animNotice && <div className="thumb-picker__hint">{animNotice}</div>}

                <div className="field">
                  <div className="upload-field-label-row">
                    <label htmlFor="anim-prompt">生成プロンプト ★</label>
                    <span className="upload-tag-count">
                      {animPrompt.length}/{MAX_ANIMATION_PROMPT_LENGTH}
                    </span>
                  </div>
                  <textarea
                    id="anim-prompt"
                    rows={3}
                    maxLength={MAX_ANIMATION_PROMPT_LENGTH}
                    value={animPrompt}
                    disabled={animRunning}
                    onChange={(e) => setAnimPrompt(e.target.value)}
                  />
                  <p className="thumb-picker__hint">
                    英語で、"A person…" のように始めると安定しやすくなります（生成モデルの学習データに合わせた書式のため）。
                  </p>
                </div>

                <div className="field">
                  <label htmlFor="anim-interval">ポーズの間隔（秒）</label>
                  <input
                    id="anim-interval"
                    type="number"
                    min={MIN_ANIMATION_INTERVAL_SEC}
                    max={MAX_ANIMATION_INTERVAL_SEC}
                    step={1}
                    value={animIntervalSec}
                    disabled={animRunning}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      if (!Number.isFinite(n)) return;
                      const clamped = Math.min(
                        MAX_ANIMATION_INTERVAL_SEC,
                        Math.max(MIN_ANIMATION_INTERVAL_SEC, Math.round(n)),
                      );
                      setAnimIntervalSec(clamped);
                    }}
                  />
                </div>

                <div className="notice">
                  生成には数分〜十数分かかります（既存サーバーの停止・生成サーバーの起動だけで数分、
                  生成本体でさらに数分。初回はモデルのダウンロードが入りさらに時間がかかることがあります）。
                  途中で固まったように見えても、ページを閉じずにお待ちください。
                </div>

                <div className="upload-image-estimate-bar">
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => setConsentTarget('animation')}
                    disabled={
                      animImages.length < MIN_ANIMATION_IMAGES ||
                      animRunning ||
                      !animPrompt.trim() ||
                      poseServerStatus === 'offline' ||
                      poseServerStatus === 'not-accepting' ||
                      poseServerStatus === 'starting'
                    }
                  >
                    アニメーションを生成する（サーバー処理）
                  </button>
                  {animPhase === 'estimating-poses' && (
                    <Spinner
                      inline
                      size="sm"
                      label={`キーポーズを推定中（${animEstimateProgress.done}/${animEstimateProgress.total}枚目）`}
                    />
                  )}
                  {animPhase === 'starting-job' && <Spinner inline size="sm" label="ジョブを開始しています…" />}
                  {animPhase === 'polling' && (
                    <Spinner
                      inline
                      size="sm"
                      label={animJobStatus?.message || poseAnimationStatusLabel(animJobStatus?.status)}
                    />
                  )}
                  {animPhase === 'building-vrma' && <Spinner inline size="sm" label="VRMA を組み立てています…" />}
                </div>

                {animPhase === 'polling' && animJobStatus?.progress && (
                  <div
                    className="upload-progress-bar"
                    role="progressbar"
                    aria-valuenow={animJobStatus.progress.current}
                    aria-valuemin={0}
                    aria-valuemax={animJobStatus.progress.total}
                  >
                    <div
                      className="upload-progress-bar__fill"
                      style={{
                        width: `${Math.round(
                          (animJobStatus.progress.current / Math.max(animJobStatus.progress.total, 1)) * 100,
                        )}%`,
                      }}
                    />
                  </div>
                )}

                {animPollWarning && <div className="notice">{animPollWarning}</div>}
                {animError && <div className="viewer__error">{animError}</div>}
              </div>

              {/* 右カラム: ポーズプレビュー。生成前は空状態、生成後は共通のプレビュー塊を表示する。 */}
              <div className="upload-image-split__right">
                {file && vrmaUrl ? (
                  <>
                    {posePreviewBlock}
                    <p className="upload-preview-meta">{previewMetaText}</p>
                  </>
                ) : (
                  <div className="upload-pose-pane-empty">
                    <p>アニメーションを生成するとここにプレビューが表示されます</p>
                  </div>
                )}
              </div>
            </div>
          ) : !file ? (
            effectiveSource === 'file' ? (
              <div className="upload-file-source">
                <div
                  className={`upload-dropzone${dragging ? ' drag' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    handleFile(e.dataTransfer.files?.[0]);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="upload-dropzone__icon" aria-hidden="true">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </div>
                  <p className="upload-dropzone__title">ここに VRMA ファイルを追加</p>
                  <p className="upload-dropzone__desc">ドラッグ＆ドロップ、またはクリックして選択</p>
                  <p className="upload-dropzone__note">追加すると VRM モデルに適用してプレビューされます</p>
                </div>
                {/* .vrma を持っていないユーザー向けに、Unity 側で変換してアップロードする経路を案内する。 */}
                <p className="upload-video-guide">
                  .vrma ファイルがなくても、Unity の Humanoid AnimationClip があれば{' '}
                  <Link to="/tools/unity">Unity ツール</Link>
                  （公式 Editor 拡張）でそのまま変換してアップロードできます。
                </p>
              </div>
            ) : (
              <div className="upload-video-source">
                <p className="upload-video-guide">
                  全身が入るように撮影し、<strong>ポーズを保ったまま動かず</strong>、カメラをゆっくり体の周り（正面側180°程度、背面は避ける）に動かしてください。多視点になるほど精度が上がります。
                </p>

                {video ? (
                  <div className="upload-file-info">
                    <div className="upload-file-info__icon" aria-hidden="true">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M4 8h3l2-2h6l2 2h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path d="m14.5 10.5 5-2.5v8l-5-2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div className="upload-file-info__meta">
                      <strong>{video.name}</strong>
                      <span>{(video.size / 1024 / 1024).toFixed(1)} MB</span>
                    </div>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => videoInputRef.current?.click()}
                      disabled={analyzing}
                    >
                      変更
                    </button>
                  </div>
                ) : (
                  <div className="upload-dropzone" onClick={() => videoInputRef.current?.click()}>
                    <div className="upload-dropzone__icon" aria-hidden="true">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M4 8h3l2-2h6l2 2h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path d="m14.5 10.5 5-2.5v8l-5-2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <p className="upload-dropzone__title">クリックして動画を選択</p>
                  </div>
                )}

                {video && videoUrl && <video className="upload-video-preview" src={videoUrl} controls />}

                <div className="upload-pbuttons">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowRecorder(true)}
                    disabled={analyzing}
                  >
                    カメラで撮影（骨格を表示）
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={analyzing}
                  >
                    端末から選択
                  </button>
                  <input ref={videoInputRef} type="file" accept="video/*" hidden onChange={onPickVideo} />
                </div>

                <div>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={handleEstimate}
                    disabled={!video || analyzing || generating}
                  >
                    {analyzing
                      ? progress.total > 0
                        ? `${progress.done} / ${progress.total} フレームを解析中…`
                        : 'モデルを準備中…（初回のみ時間がかかります）'
                      : 'ポーズを推定する'}
                  </button>
                  {analyzing && (
                    <button
                      type="button"
                      className="btn"
                      onClick={handleCancelEstimate}
                      style={{ marginLeft: 10 }}
                    >
                      キャンセル
                    </button>
                  )}
                  {generating && (
                    <span className="thumb-picker__hint" style={{ marginLeft: 10 }}>
                      VRMA を生成中…
                    </span>
                  )}
                </div>

                {analyzing && progress.total > 0 && (
                  <div
                    className="upload-progress-bar"
                    role="progressbar"
                    aria-valuenow={progress.done}
                    aria-valuemin={0}
                    aria-valuemax={progress.total}
                  >
                    <div
                      className="upload-progress-bar__fill"
                      style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                    />
                  </div>
                )}

                {poseError && <div className="viewer__error">{poseError}</div>}
              </div>
            )
          ) : (
            <>
              {posePreviewBlock}

              <p className="upload-preview-meta">{previewMetaText}</p>

              {effectiveSource === 'video' && (
                <div className="upload-psplit">
                  <label className="radio">
                    <input
                      type="checkbox"
                      checked={mirror}
                      onChange={handleToggleMirror}
                      disabled={generating}
                    />
                    左右反転
                  </label>
                  <button
                    type="button"
                    className="btn"
                    onClick={handleEstimate}
                    disabled={analyzing || generating || !video}
                  >
                    再生成
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        {/* ---- 右ペイン: 利用条件・表示設定・投稿 ---- */}
        <section className="upload-pane upload-pane--right">
          <div className="upload-card">
            <h2 className="upload-card__title">アニメーション登録者以外の利用</h2>
            <p className="upload-usage-sub">{USAGE_TERMS_LEAD_TEXT}</p>
            <ul className="upload-usage-summary">
              {usageSummaryRows(usageTerms).map((row) => (
                <li key={row.label}>
                  <span>{row.label}</span>
                  <span className={row.ok ? 'is-ok' : 'is-ng'}>
                    {row.ok ? (row.okText ?? 'OK') : (row.ngText ?? 'NG')}
                  </span>
                </li>
              ))}
            </ul>
            <button type="button" className="btn upload-pillbtn" onClick={() => setShowUsageModal(true)}>
              利用条件を変更
            </button>
          </div>

          <div className="upload-card">
            <h2 className="upload-card__title">ポーズの表示設定 ★</h2>
            <ul className="upload-display-summary">
              <li>
                <span>公開範囲</span>
                <span>{visibility === 'public' ? '公開' : '非公開'}</span>
              </li>
              <li>
                <span>閲覧制限</span>
                <span>{rating === 'all' ? '全年齢' : 'センシティブ'}</span>
              </li>
            </ul>
            <button type="button" className="btn upload-pillbtn" onClick={() => setShowDisplayModal(true)}>
              表示設定を変更
            </button>
          </div>

          <div className="upload-footer-actions">
            {!isEdit && user && <div className="stat">投稿者: @{user.name} として送信されます。</div>}
            <div className="upload-footer-actions__row">
              <button type="button" className="upload-cancel-link" onClick={handleCancelPage}>
                キャンセル
              </button>
              <button
                type="submit"
                form="pose-upload-form"
                className="btn btn--primary"
                disabled={isEdit ? submitting : submitting || !file}
              >
                {isEdit
                  ? submitting
                    ? '保存中…'
                    : '変更を保存'
                  : submitting
                    ? '送信中…'
                    : visibility === 'public'
                      ? '公開で登録'
                      : '非公開で登録'}
              </button>
            </div>
          </div>
        </section>
      </div>

      <PublishConfirmModal
        open={showPublishConfirm}
        onCancel={handleCancelPublish}
        onConfirm={handleConfirmPublish}
        confirmLabel={isEdit ? '公開して保存する' : '公開して投稿する'}
        confirming={submitting}
      />

      <PhotoConsentModal
        open={consentTarget !== null}
        onCancel={handleCancelPhotoConsent}
        onConfirm={handleConfirmPhotoConsent}
      />

      <UsageTermsModal
        open={showUsageModal}
        value={usageTerms}
        onCancel={() => setShowUsageModal(false)}
        onConfirm={(next) => {
          setUsageTerms(next);
          setShowUsageModal(false);
        }}
      />

      <DisplaySettingsModal
        open={showDisplayModal}
        visibility={visibility}
        rating={rating}
        onCancel={() => setShowDisplayModal(false)}
        onConfirm={({ visibility: v, rating: r }) => {
          setVisibility(v);
          setRating(r);
          setShowDisplayModal(false);
        }}
      />

      {!isEdit && poseGenerationEnabled && showRecorder && (
        <PoseRecorder
          onClose={() => setShowRecorder(false)}
          onRecorded={(f) => {
            acceptVideoFile(f);
            setShowRecorder(false);
          }}
        />
      )}
    </div>
  );
}
